import { useEffect, useState, useCallback } from "react";
import { FileText, ExternalLink } from "lucide-react";
import { adminFetch, adminFileUrl } from "@/lib/api";
import { Loader, Card, EmptyState } from "@/components/common";

const DOC_TYPES = ["", "DDT", "CMR", "Allegato viaggio", "Allegato missione", "Allegato chat", "Documento"];
const ROLES = [["", "Tutti i ruoli"], ["azienda", "Azienda"], ["vettore", "Vettore"], ["autista", "Autista"]];

export default function AdminDocuments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [role, setRole] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (type) params.set("type", type);
      if (role) params.set("role", role);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const res = await adminFetch(`/admin/documents?${params.toString()}`);
      setItems(res.items || []);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [q, type, role, dateFrom, dateTo]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const selectClass = "min-h-[42px] rounded-xl border border-border bg-card px-3 text-sm outline-none focus:border-primary";

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <input data-testid="admin-docs-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome file..." className="col-span-2 min-h-[42px] rounded-xl border border-border bg-card px-3 text-sm outline-none focus:border-primary sm:col-span-1" />
        <select data-testid="admin-docs-type" value={type} onChange={(e) => setType(e.target.value)} className={selectClass}>
          {DOC_TYPES.map((t) => <option key={t} value={t}>{t || "Tutti i tipi"}</option>)}
        </select>
        <select data-testid="admin-docs-role" value={role} onChange={(e) => setRole(e.target.value)} className={selectClass}>
          {ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input data-testid="admin-docs-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={selectClass} />
        <input data-testid="admin-docs-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={selectClass} />
      </div>

      {loading ? (
        <Loader />
      ) : items.length === 0 ? (
        <EmptyState icon={FileText} title="Nessun documento" text="Nessun documento corrisponde ai filtri selezionati." />
      ) : (
        items.map((d) => (
          <Card key={d.attachment_id} data-testid={`admin-doc-${d.attachment_id}`} className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-secondary">
              <FileText className="h-5 w-5 text-primary" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[14px] font-extrabold text-foreground">
                <span className="mr-2 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-extrabold text-primary">{d.type}</span>
                {d.name}
              </p>
              <p className="truncate text-[12px] text-muted-foreground">
                {d.context_label || d.context}
                {d.uploaded_by ? ` · caricato da ${d.uploaded_by.name || "—"} (${d.uploaded_by.role || "?"})` : ""}
                {` · ${String(d.created_at || "").slice(0, 16).replace("T", " ")}`}
              </p>
              {d.involved?.length ? (
                <p className="truncate text-[11px] text-muted-foreground">Coinvolti: {d.involved.map((i) => i?.name).filter(Boolean).join(", ")}</p>
              ) : null}
            </div>
            <a href={adminFileUrl(d.attachment_id)} target="_blank" rel="noreferrer" data-testid={`admin-doc-open-${d.attachment_id}`} className="flex shrink-0 items-center gap-1 rounded-lg border border-border px-3 py-2 text-xs font-extrabold text-primary hover:bg-secondary">
              <ExternalLink className="h-3.5 w-3.5" /> Apri
            </a>
          </Card>
        ))
      )}
    </div>
  );
}
